<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  mdiFile,
  mdiFileImage,
  mdiFileVideo,
  mdiFileMusic,
  mdiFileDocument,
  mdiFilePdfBox,
  mdiPause,
  mdiPlay,
  mdiClose,
  mdiRefresh,
  mdiCheck,
  mdiWalletPlusOutline,
} from '@mdi/js'
import { useCreditsExhaustedStore } from '@/apps/billing/stores/creditsExhausted'
import { useUploadStore } from '../../stores/useUploadStore'
import type { UploadItem } from '../../types/upload.types'

interface Props {
  upload: UploadItem
}

interface Emits {
  (e: 'pause', id: string): void
  (e: 'resume', id: string): void
  (e: 'cancel', id: string): void
  (e: 'retry', id: string): void
  (e: 'remove', id: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()

const thumbnailUrl = ref<string | null>(null)
const showSuccessAnimation = ref(false)
const previousStatus = ref(props.upload.status)

const fileTypeCategory = computed(() => {
  const type = props.upload.metadata.contentType
  if (type.startsWith('image/')) return 'image'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'
  if (type === 'application/pdf') return 'pdf'
  if (
    type.includes('document') ||
    type.includes('text') ||
    type.includes('word') ||
    type.includes('sheet')
  ) {
    return 'document'
  }
  return 'other'
})

const fileIcon = computed(() => {
  switch (fileTypeCategory.value) {
    case 'image':
      return mdiFileImage
    case 'video':
      return mdiFileVideo
    case 'audio':
      return mdiFileMusic
    case 'pdf':
      return mdiFilePdfBox
    case 'document':
      return mdiFileDocument
    default:
      return mdiFile
  }
})

const typeColor = computed(() => {
  switch (fileTypeCategory.value) {
    case 'video':
      return 'rgb(var(--v-theme-primary))'
    case 'image':
      return 'rgb(var(--v-theme-success))'
    case 'audio':
      return 'rgb(var(--v-theme-info))'
    case 'pdf':
      return 'rgb(var(--v-theme-error))'
    case 'document':
      return 'rgb(var(--v-theme-warning))'
    default:
      return 'rgb(var(--v-theme-on-surface))'
  }
})

const canShowThumbnail = computed(() => {
  return fileTypeCategory.value === 'image'
})

const formattedSize = computed(() => {
  const bytes = props.upload.bytesTotal
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
})

const statusText = computed(() => {
  switch (props.upload.status) {
    case 'pending':
      return t('assets.upload.item.queued')
    case 'uploading':
      return `${props.upload.progress}%`
    case 'paused':
      return t('assets.upload.item.paused')
    case 'completed':
      return t('assets.upload.item.completed')
    case 'failed':
      return t('assets.upload.item.failed')
    case 'cancelled':
      return t('assets.upload.item.cancelled')
    default:
      return ''
  }
})

const failureText = computed(() => {
  if (props.upload.failureKind) return t(`assets.upload.failure.${props.upload.failureKind}`)
  return props.upload.error
})

// A refusal for want of credit opens the one « insufficient credits » modal, whose top-up sends
// every upload the credit refused again; a member is told there whom to ask.
const uploadStore = useUploadStore()
const credits = useCreditsExhaustedStore()
const refusedForCredit = computed(
  () => props.upload.status === 'failed' && props.upload.failureKind === 'creditsExhausted',
)
function topUp(): void {
  void credits.report({ retry: async () => uploadStore.retryCreditRefused() })
}

const isActive = computed(() => {
  return ['uploading', 'paused', 'pending'].includes(props.upload.status)
})

function createThumbnail() {
  if (canShowThumbnail.value && props.upload.file) {
    thumbnailUrl.value = URL.createObjectURL(props.upload.file)
  }
}

function revokeThumbnail() {
  if (thumbnailUrl.value) {
    URL.revokeObjectURL(thumbnailUrl.value)
    thumbnailUrl.value = null
  }
}

const checkStatusChange = () => {
  if (previousStatus.value !== 'completed' && props.upload.status === 'completed') {
    showSuccessAnimation.value = true
    setTimeout(() => {
      showSuccessAnimation.value = false
    }, 1200)
  }
  previousStatus.value = props.upload.status
}

onMounted(() => {
  createThumbnail()
})

onUnmounted(() => {
  revokeThumbnail()
})

watch(() => props.upload.status, checkStatusChange)
</script>

<template>
  <div
    class="upload-item"
    :class="{
      'upload-item--success': showSuccessAnimation,
      'upload-item--failed': upload.status === 'failed',
      'upload-item--completed': upload.status === 'completed',
    }"
  >
    <!-- Thumbnail with Progress -->
    <div class="upload-item__visual">
      <!-- Progress Ring (behind thumbnail) -->
      <svg
        v-if="isActive"
        class="upload-item__progress-ring"
        viewBox="0 0 44 44"
      >
        <circle
          class="upload-item__progress-bg"
          cx="22"
          cy="22"
          r="20"
          fill="none"
          stroke-width="2"
        />
        <circle
          class="upload-item__progress-bar"
          cx="22"
          cy="22"
          r="20"
          fill="none"
          stroke-width="2"
          :stroke-dasharray="125.6"
          :stroke-dashoffset="125.6 - (125.6 * upload.progress) / 100"
          :style="{ stroke: typeColor }"
        />
      </svg>

      <!-- Thumbnail -->
      <div class="upload-item__thumbnail" :style="{ '--type-color': typeColor }">
        <img
          v-if="thumbnailUrl"
          :src="thumbnailUrl"
          class="upload-item__thumbnail-img"
          alt=""
        />
        <v-icon
          v-else
          :icon="fileIcon"
          size="20"
          class="upload-item__thumbnail-icon"
        />

        <!-- Success Check -->
        <Transition name="scale">
          <div v-if="showSuccessAnimation" class="upload-item__success-check">
            <v-icon :icon="mdiCheck" size="16" color="white" />
          </div>
        </Transition>
      </div>
    </div>

    <!-- Info -->
    <div class="upload-item__info">
      <span class="upload-item__name">{{ upload.metadata.filename }}</span>
      <span class="upload-item__meta">
        <span>{{ formattedSize }}</span>
        <span class="upload-item__separator">•</span>
        <span
          class="upload-item__status"
          :class="{
            'upload-item__status--error': upload.status === 'failed',
            'upload-item__status--success': upload.status === 'completed',
            'upload-item__status--warning': upload.status === 'paused',
          }"
        >
          {{ statusText }}
        </span>
      </span>
      <!-- The sentence for the person, the raw message on hover for whoever answers a support
           request about it. -->
      <span v-if="failureText" class="upload-item__error" :title="upload.error ?? undefined">
        {{ failureText }}
      </span>
    </div>

    <!-- Actions -->
    <div class="upload-item__actions">
      <template v-if="upload.status === 'uploading'">
        <v-btn
          v-if="upload.uploadMethod !== 'direct'"
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.pause')"
          @click="emit('pause', upload.id)"
        >
          <v-icon :icon="mdiPause" size="18" />
        </v-btn>
        <v-btn
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.cancel')"
          @click="emit('cancel', upload.id)"
        >
          <v-icon :icon="mdiClose" size="18" />
        </v-btn>
      </template>

      <template v-else-if="upload.status === 'paused'">
        <v-btn
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.resume')"
          @click="emit('resume', upload.id)"
        >
          <v-icon :icon="mdiPlay" size="18" />
        </v-btn>
        <v-btn
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.cancel')"
          @click="emit('cancel', upload.id)"
        >
          <v-icon :icon="mdiClose" size="18" />
        </v-btn>
      </template>

      <template v-else-if="upload.status === 'pending'">
        <v-btn
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.cancel')"
          @click="emit('cancel', upload.id)"
        >
          <v-icon :icon="mdiClose" size="18" />
        </v-btn>
      </template>

      <template v-else-if="upload.status === 'failed'">
        <!-- The credit refused it: the remedy is a top-up, which sends it again once it lands. -->
        <v-btn
          v-if="refusedForCredit"
          icon
          size="x-small"
          variant="text"
          color="primary"
          :title="t('assets.upload.actions.topUp')"
          :aria-label="t('assets.upload.actions.topUp')"
          @click="topUp"
        >
          <v-icon :icon="mdiWalletPlusOutline" size="18" />
        </v-btn>
        <v-btn
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.retry')"
          @click="emit('retry', upload.id)"
        >
          <v-icon :icon="mdiRefresh" size="18" />
        </v-btn>
        <v-btn
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.remove')"
          @click="emit('remove', upload.id)"
        >
          <v-icon :icon="mdiClose" size="18" />
        </v-btn>
      </template>

      <template v-else-if="upload.status === 'completed'">
        <v-icon :icon="mdiCheck" size="18" color="success" class="mr-1" />
        <v-btn
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.remove')"
          @click="emit('remove', upload.id)"
        >
          <v-icon :icon="mdiClose" size="18" />
        </v-btn>
      </template>

      <template v-else-if="upload.status === 'cancelled'">
        <v-btn
          icon
          size="x-small"
          variant="text"
          :title="t('assets.upload.actions.remove')"
          @click="emit('remove', upload.id)"
        >
          <v-icon :icon="mdiClose" size="18" />
        </v-btn>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.upload-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin: 0 8px;
  border-radius: 10px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.03);
  }

  &--success {
    animation: success-pulse 1.2s ease-out;
  }

  &--failed {
    background-color: rgba(var(--v-theme-error), 0.04);
  }

  &__visual {
    position: relative;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
  }

  &__progress-ring {
    position: absolute;
    top: 0;
    left: 0;
    width: 44px;
    height: 44px;
    transform: rotate(-90deg);
  }

  &__progress-bg {
    stroke: rgba(var(--v-theme-on-surface), 0.08);
  }

  &__progress-bar {
    transition: stroke-dashoffset 0.3s ease;
    stroke-linecap: round;
  }

  &__thumbnail {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-on-surface), 0.06),
      rgba(var(--v-theme-on-surface), 0.02)
    );
  }

  &__thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__thumbnail-icon {
    color: var(--type-color);
    opacity: 0.8;
  }

  &__success-check {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgb(var(--v-theme-success));
    border-radius: 8px;
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.87);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__separator {
    opacity: 0.5;
  }

  &__status {
    &--error {
      color: rgb(var(--v-theme-error));
    }

    &--success {
      color: rgb(var(--v-theme-success));
    }

    &--warning {
      color: rgb(var(--v-theme-warning));
    }
  }

  &__error {
    font-size: 11px;
    color: rgb(var(--v-theme-error));
    margin-top: 2px;
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover &__actions {
    opacity: 1;
  }
}

@keyframes success-pulse {
  0% {
    background-color: transparent;
  }
  30% {
    background-color: rgba(var(--v-theme-success), 0.12);
  }
  100% {
    background-color: transparent;
  }
}

// Scale transition for success check
.scale-enter-active {
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.scale-leave-active {
  transition: all 0.2s ease-out;
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.5);
}
</style>
